export type InsightStatus = "checking" | "okay" | "failed" | "na";

export type CallInsightTelemetry = {
    callId?: string;
    pGuid?: string;
    timestamp?: string;
    deviceCpuUsage?: number | null;
    deviceMemoryUsedPct?: number | null;
    wifiSignalQuality?: number | string | null;
    audioJitter?: number | null;
    audioJBDelay?: number | null;
    audioRecvLossRate?: number | null;
    audioRtt?: number | null;
    videoRtt?: number | null;
    videoRecvLossRate?: number | null;
    wifiChannelCenterFrequencyInKilohertz?: number | null;
    wifiSsidChangeDetected?: boolean | null;
    deviceName?: string | null;
    networkId?: string | null;
    isManagedNetwork?: boolean;
    ssid?: string | null;
    bssid?: string | null;
};

type ParameterName =
    | "deviceCpuUsage"
    | "deviceMemoryUsedPct"
    | "wifiSignalQuality"
    | "audioJitter"
    | "audioJBDelay"
    | "audioRecvLossRate"
    | "audioRtt"
    | "videoRtt"
    | "videoRecvLossRate"
    | "wifiChannelCenterFrequencyInKilohertz"
    | "wifiSsidChangeDetected"
    | "deviceUsageQuality"
    | "audioQuality"
    | "videoQuality"
    | "networkQuality"
    | "deviceName"
    | "networkId"
    | "isManagedNetwork"
    | "ssid"
    | "bssid";

export type InsightItem = {
    propertyName: ParameterName;
    displayName: string;
    status: InsightStatus;
    rootCause: string;
    recommendation: string;
    threshold: string;
    observedValue: number | boolean | string | null;
};

export type InsightsReport = {
    callId: string | null;
    sampleCount: number;
    status: InsightStatus;
    insights: InsightItem[];
    networkId: string | null;
    bssid: string | null;
    isManagedNetwork: boolean;
};

type AggregatedMetrics = {
    maxDeviceCpuUsage: number | null;
    maxDeviceMemoryUsedPct: number | null;
    avgWifiSignalQuality: number | null;
    maxAudioJitter: number | null;
    maxAudioJBDelay: number | null;
    maxAudioRecvLossRate: number | null;
    maxAudioRtt: number | null;
    maxVideoRtt: number | null;
    maxVideoRecvLossRate: number | null;
    minWifiChannelCenterFrequencyInKilohertz: number | null;
    wifiSsidChangeDetected: boolean;
    deviceName: string | null;
    networkId: string | null;
    isManagedNetwork: boolean;
    ssid: string | null;
    bssid: string | null;
};

const LOW_WIFI_ROOT_CAUSE = "chatBot.teamsInsights.report.lowWifiRootCause";

const NETWORK_ROOT_CAUSE_MANAGED = "chatBot.teamsInsights.report.networkIssueRootCauseManaged";

const NETWORK_ROOT_CAUSE_UNMANAGED = "chatBot.teamsInsights.report.networkIssueRootCauseUnmanaged";

const NO_DATA_ROOT_CAUSE = "chatBot.teamsInsights.report.noDataRootCause";

const toNumber = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const updateMax = (current: number | null, next: number | null): number | null => {
    if (next === null) {
        return current;
    }

    if (current === null) {
        return next;
    }

    return Math.max(current, next);
};

const updateMin = (current: number | null, next: number | null): number | null => {
    if (next === null) {
        return current;
    }

    if (current === null) {
        return next;
    }

    return Math.min(current, next);
};

const buildNumericInsight = (
    propertyName: ParameterName,
    displayName: string,
    observedValue: number | null | string,
    threshold: string,
    isFailed: (value: number) => boolean,
    rootCause: string,
    recommendation: string
): InsightItem => {
    if (observedValue === null) {
        return {
            propertyName,
            displayName,
            status: "na",
            rootCause: NO_DATA_ROOT_CAUSE,
            recommendation: NO_DATA_ROOT_CAUSE,
            threshold,
            observedValue,
        };
    }

    if (isFailed(observedValue)) {
        return {
            propertyName,
            displayName,
            status: "failed",
            rootCause,
            recommendation,
            threshold,
            observedValue,
        };
    }

    return {
        propertyName,
        displayName,
        status: "okay",
        rootCause: "chatBot.teamsInsights.report.noIssueRootCause",
        recommendation: "chatBot.teamsInsights.report.noIssueRecommendation",
        threshold,
        observedValue,
    };
};

const aggregateMetrics = (insights: CallInsightTelemetry[]): AggregatedMetrics => {
    let wifiSignalQualitySum = 0;
    let wifiSignalQualityCount = 0;

    const aggregated: AggregatedMetrics = {
        maxDeviceCpuUsage: null,
        maxDeviceMemoryUsedPct: null,
        avgWifiSignalQuality: null,
        maxAudioJitter: null,
        maxAudioJBDelay: null,
        maxAudioRecvLossRate: null,
        maxAudioRtt: null,
        maxVideoRtt: null,
        maxVideoRecvLossRate: null,
        minWifiChannelCenterFrequencyInKilohertz: null,
        wifiSsidChangeDetected: false,
        deviceName: null,
        networkId: null,
        isManagedNetwork: false,
        ssid: null,
        bssid: null
    };

    for (const insight of insights) {
        aggregated.maxDeviceCpuUsage = updateMax(
            aggregated.maxDeviceCpuUsage,
            toNumber(insight.deviceCpuUsage)
        );
        aggregated.maxDeviceMemoryUsedPct = updateMax(
            aggregated.maxDeviceMemoryUsedPct,
            toNumber(insight.deviceMemoryUsedPct)
        );
        aggregated.maxAudioJitter = updateMax(
            aggregated.maxAudioJitter,
            toNumber(insight.audioJitter)
        );
        aggregated.maxAudioJBDelay = updateMax(
            aggregated.maxAudioJBDelay,
            toNumber(insight.audioJBDelay)
        );
        aggregated.maxAudioRecvLossRate = updateMax(
            aggregated.maxAudioRecvLossRate,
            toNumber(insight.audioRecvLossRate)
        );
        aggregated.maxAudioRtt = updateMax(
            aggregated.maxAudioRtt,
            toNumber(insight.audioRtt)
        );
        aggregated.maxVideoRtt = updateMax(
            aggregated.maxVideoRtt,
            toNumber(insight.videoRtt)
        );
        aggregated.maxVideoRecvLossRate = updateMax(
            aggregated.maxVideoRecvLossRate,
            toNumber(insight.videoRecvLossRate)
        );
        aggregated.minWifiChannelCenterFrequencyInKilohertz = updateMin(
            aggregated.minWifiChannelCenterFrequencyInKilohertz,
            toNumber(insight.wifiChannelCenterFrequencyInKilohertz)
        );

        const wifiSignalValue = toNumber(insight.wifiSignalQuality);
        if (wifiSignalValue !== null) {
            wifiSignalQualitySum += wifiSignalValue;
            wifiSignalQualityCount += 1;
        }

        if (insight.wifiSsidChangeDetected === true) {
            aggregated.wifiSsidChangeDetected = true;
        }

        if (insight.deviceName) {
            aggregated.deviceName = insight.deviceName;
        }

        if (insight.networkId) {
            aggregated.networkId = insight.networkId;
        }

        if (insight.isManagedNetwork !== undefined) {
            aggregated.isManagedNetwork = insight.isManagedNetwork;
        }

        if (insight.ssid) {
            aggregated.ssid = insight.ssid;
        }

        if (insight.bssid) {
            aggregated.bssid = insight.bssid;
        }
    }

    if (wifiSignalQualityCount > 0) {
        aggregated.avgWifiSignalQuality = wifiSignalQualitySum / wifiSignalQualityCount;
    }

    return aggregated;
};

const checkDeviceCpuUsage = (metrics: AggregatedMetrics): InsightItem =>
    buildNumericInsight(
        "deviceCpuUsage",
        "chatBot.teamsInsights.report.deviceCpuUsage.displayName",
        metrics.maxDeviceCpuUsage,
        "Max Value during Call > 90",
        (value) => value > 90,
        "chatBot.teamsInsights.report.deviceCpuUsage.rootCause",
        "chatBot.teamsInsights.report.deviceCpuUsage.recommendation"
    );

const checkDeviceMemoryUsedPct = (metrics: AggregatedMetrics): InsightItem =>
    buildNumericInsight(
        "deviceMemoryUsedPct",
        "chatBot.teamsInsights.report.deviceMemoryUsedPct.displayName",
        metrics.maxDeviceMemoryUsedPct,
        "Max Value during Call > 90",
        (value) => value > 90,
        "chatBot.teamsInsights.report.deviceMemoryUsedPct.rootCause",
        "chatBot.teamsInsights.report.deviceMemoryUsedPct.recommendation"
    );

const checkWifiSignalQuality = (metrics: AggregatedMetrics): InsightItem =>
    buildNumericInsight(
        "wifiSignalQuality",
        "chatBot.teamsInsights.report.wifiSignalQuality.displayName",
        metrics.avgWifiSignalQuality,
        "Average During Call > 50",
        (value) => value <= 50,
        LOW_WIFI_ROOT_CAUSE,
        "chatBot.teamsInsights.report.wifiSignalQuality.recommendation"
    );

const checkAudioJitter = (metrics: AggregatedMetrics): InsightItem =>
    buildNumericInsight(
        "audioJitter",
        "chatBot.teamsInsights.report.audioJitter.displayName",
        metrics.maxAudioJitter,
        "Max Value during Call > 30",
        (value) => value > 30,
        getNetworkRootCause(metrics.isManagedNetwork),
        getNetworkRootCause(metrics.isManagedNetwork)
    );

const checkAudioJBDelay = (metrics: AggregatedMetrics): InsightItem =>
    buildNumericInsight(
        "audioJBDelay",
        "chatBot.teamsInsights.report.audioJBDelay.displayName",
        metrics.maxAudioJBDelay,
        "Max Value during Call > 400",
        (value) => value > 400,
        getNetworkRootCause(metrics.isManagedNetwork),
        getNetworkRootCause(metrics.isManagedNetwork)
    );

const checkAudioRecvLossRate = (metrics: AggregatedMetrics): InsightItem =>
    buildNumericInsight(
        "audioRecvLossRate",
        "chatBot.teamsInsights.report.audioRecvLossRate.displayName",
        metrics.maxAudioRecvLossRate,
        "Max Value during Call > 10.0",
        (value) => value > 10,
        getNetworkRootCause(metrics.isManagedNetwork),
        getNetworkRootCause(metrics.isManagedNetwork)
    );

const checkAudioRtt = (metrics: AggregatedMetrics): InsightItem =>
    buildNumericInsight(
        "audioRtt",
        "chatBot.teamsInsights.report.audioRtt.displayName",
        metrics.maxAudioRtt,
        "Max Value during Call > 400",
        (value) => value > 400,
        getNetworkRootCause(metrics.isManagedNetwork),
        getNetworkRootCause(metrics.isManagedNetwork)
    );

const checkVideoRtt = (metrics: AggregatedMetrics): InsightItem =>
    buildNumericInsight(
        "videoRtt",
        "chatBot.teamsInsights.report.videoRtt.displayName",
        metrics.maxVideoRtt,
        "Max Value during Call > 400",
        (value) => value > 400,
        getNetworkRootCause(metrics.isManagedNetwork),
        getNetworkRootCause(metrics.isManagedNetwork)
    );

const checkVideoRecvLossRate = (metrics: AggregatedMetrics): InsightItem =>
    buildNumericInsight(
        "videoRecvLossRate",
        "chatBot.teamsInsights.report.videoRecvLossRate.displayName",
        metrics.maxVideoRecvLossRate,
        "Max Value during Call > 10.0",
        (value) => value > 10,
        getNetworkRootCause(metrics.isManagedNetwork),
        getNetworkRootCause(metrics.isManagedNetwork)
    );

const checkWifiChannelCenterFrequencyInKilohertz = (
    metrics: AggregatedMetrics
): InsightItem =>
    buildNumericInsight(
        "wifiChannelCenterFrequencyInKilohertz",
        "chatBot.teamsInsights.report.wifiChannelCenterFrequencyInKilohertz.displayName",
        metrics.minWifiChannelCenterFrequencyInKilohertz,
        "Min Value during Call >= 4000000",
        (value) => value < 4000000,
        "chatBot.teamsInsights.report.wifiChannelCenterFrequencyInKilohertz.rootCause",
        "chatBot.teamsInsights.report.wifiChannelCenterFrequencyInKilohertz.recommendation"
    );

const checkWifiSsidChangeDetected = (metrics: AggregatedMetrics): InsightItem => {
    if (metrics.wifiSsidChangeDetected) {

        const ROOT_CAUSE_MANAGED = "chatBot.teamsInsights.report.wifiSsidChangeDetected.rootCauseManaged";
        const ROOT_CAUSE_UNMANAGED = "chatBot.teamsInsights.report.wifiSsidChangeDetected.rootCauseUnmanaged";

        return {
            propertyName: "wifiSsidChangeDetected",
            displayName: "chatBot.teamsInsights.report.wifiSsidChangeDetected.displayName",
            status: "failed",
            rootCause: metrics.isManagedNetwork ? ROOT_CAUSE_MANAGED : ROOT_CAUSE_UNMANAGED,
            recommendation: metrics.isManagedNetwork ? ROOT_CAUSE_MANAGED : ROOT_CAUSE_UNMANAGED,
            threshold: "If anytime during Call = true",
            observedValue: true,
        };
    }

    return {
        propertyName: "wifiSsidChangeDetected",
        displayName: "chatBot.teamsInsights.report.wifiSsidChangeDetected.displayName",
        status: "okay",
        rootCause: "chatBot.teamsInsights.report.noIssueRootCause",
        recommendation: "chatBot.teamsInsights.report.noIssueRecommendation",
        threshold: "If anytime during Call = true",
        observedValue: false,
    };
};

const deriveOverallStatus = (items: InsightItem[]): InsightStatus => {
    if (items.some((item) => item.status === "failed")) {
        return "failed";
    }

    if (items.some((item) => item.status === "checking")) {
        return "checking";
    }

    return "okay";
};

const buildGroupedNumericInsight = (propertyName: ParameterName, displayName: string, metrics: InsightItem[]): InsightItem => {
    const rootCause = metrics.find((metric) => metric.status === "failed")?.rootCause ?? "chatBot.teamsInsights.report.noIssueRootCause";
    const recommendation = metrics.find((metric) => metric.status === "failed")?.recommendation ?? "chatBot.teamsInsights.report.noIssueRecommendation";
    const threshold = metrics.map((metric) => metric.threshold).join("\n");
    const observedValue = metrics.map((metric) => `${metric.displayName}: ${metric.observedValue}`).join("\n");
    const isFailed = () => metrics.some((metric) => metric.status === "failed");
    
    return buildNumericInsight(
        propertyName,
        displayName,
        observedValue,
        threshold,
        isFailed,
        rootCause,
        recommendation
    );
};

const getNetworkRootCause = (isManagedNetwork: boolean): string => {
    return isManagedNetwork ? NETWORK_ROOT_CAUSE_MANAGED : NETWORK_ROOT_CAUSE_UNMANAGED;
}

const checkAudioQuality = (metrics: AggregatedMetrics): InsightItem => {

    const audioQualityMetrics = [
        checkAudioJitter(metrics),
        checkAudioJBDelay(metrics),
        checkAudioRecvLossRate(metrics),
        checkAudioRtt(metrics)
    ];

    return buildGroupedNumericInsight(
        "audioQuality",
        "chatBot.teamsInsights.report.audio",
        audioQualityMetrics
    );
};

const checkVideoQuality = (metrics: AggregatedMetrics): InsightItem => {

    const videoQualityMetrics = [
        checkVideoRtt(metrics),
        checkVideoRecvLossRate(metrics)
    ];

    return buildGroupedNumericInsight(
        "videoQuality",
        "chatBot.teamsInsights.report.video",
        videoQualityMetrics
    );
};

export const getInsightsReport = (insights: CallInsightTelemetry[]): InsightsReport => {
    const safeInsights = insights ?? [];
    const metrics = aggregateMetrics(safeInsights);

    const reportItems: InsightItem[] = [
        checkDeviceCpuUsage(metrics),
        checkDeviceMemoryUsedPct(metrics),
        checkWifiSignalQuality(metrics),
        checkAudioQuality(metrics),
        checkVideoQuality(metrics),
        checkWifiChannelCenterFrequencyInKilohertz(metrics),
        checkWifiSsidChangeDetected(metrics)
    ];

    return {
        callId: safeInsights[0]?.callId ?? null,
        networkId: metrics.networkId ?? null,
        bssid: metrics.bssid ?? null,
        isManagedNetwork: metrics.isManagedNetwork ?? true,
        sampleCount: safeInsights.length,
        status: deriveOverallStatus(reportItems),
        insights: reportItems,
    };
};
